import { Modal, Space, Typography } from 'antd'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const { Text } = Typography

type Props<V> = {
  open: boolean
  title: string
  /** Body copy explaining the consequence of the action. */
  description: ReactNode
  okText: string
  okType?: 'primary' | 'danger'
  loading?: boolean
  /** Label shown above the optional field. */
  fieldLabel: string
  /** Value the field resets to whenever the modal (re)opens. */
  emptyValue: V
  renderField: (value: V, onChange: (next: V) => void) => ReactNode
  onConfirm: (value: V) => void
  onCancel: () => void
}

/**
 * Shared confirmation modal for credential actions (revoke / rotate). Each
 * captures a single optional field; the shell — cancel-while-loading guard,
 * optional-field label, danger styling — is identical across surfaces.
 *
 * The field resets on every (re)open (adjusting state during render), which is
 * robust against reopening during the close animation — unlike an
 * `afterOpenChange`-only reset, where a draft could leak into the next
 * credential.
 */
export const CredentialActionModal = <V,>({
  open,
  title,
  description,
  okText,
  okType = 'primary',
  loading,
  fieldLabel,
  emptyValue,
  renderField,
  onConfirm,
  onCancel
}: Props<V>) => {
  const { t } = useTranslation()
  const [value, setValue] = useState<V>(emptyValue)

  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setValue(emptyValue)
  }

  return (
    <Modal
      open={open}
      title={title}
      okText={okText}
      okType={okType}
      cancelText={t('cancel', 'Cancel')}
      confirmLoading={loading}
      onOk={() => onConfirm(value)}
      onCancel={() => {
        if (!loading) onCancel()
      }}
      destroyOnHidden
    >
      <Space vertical size={12} style={{ width: '100%' }}>
        {typeof description === 'string' ? (
          <Text>{description}</Text>
        ) : (
          description
        )}
        <div style={{ width: '100%' }}>
          <Text
            type="secondary"
            style={{ fontSize: 12, display: 'block', marginBottom: 4 }}
          >
            {fieldLabel} ({t('credentials.optional')})
          </Text>
          {renderField(value, setValue)}
        </div>
      </Space>
    </Modal>
  )
}
